import classNames from 'classnames';
import React from 'react';

import { Accordion } from '@/design-system/atoms/Accordion';
import { Button } from '@/design-system/atoms/Button';
import { RadioGroup } from '@/design-system/atoms/Forms/RadioGroup';
import { Radio } from '@/design-system/atoms/Forms/RadioGroup/Radio/Radio';
import { LazyIconXCircle } from '@/design-system/atoms/Icons/IconXCircle/Lazy';
import { useSearchParams } from '@/hooks/useSearchParams';

import { useLocationSearchContext } from '../../../contexts/LocationSearchContext';
import { useLocationUIContext } from '../../../contexts/LocationUIContext';
import { useLocationViewState } from '../../../hooks/useLocationViewState';
import styles from '../LocationFilter.module.scss';

const LocationFilterDesktop = ({
    accordionItems,
    sortValue,
    sortCriteria,
    handleSort,
    distance,
    setDistance,
    disabledDistanceOptions,
    handleSingleFilter,
    handleResetAll,
}) => {
    const { resetAllLabel, resetAllAriaLabel, filterLegendLabelList, filterLegendLabelMap } =
        useLocationUIContext();
    const searchParams = useSearchParams();

    const {
        coveo: {
            controller: { breadcrumbManager },
        },
    } = useLocationSearchContext();

    const viewState = useLocationViewState();

    const [state, setState] = React.useState();
    React.useEffect(() => {
        return breadcrumbManager.subscribe(() => setState(breadcrumbManager.state));
    }, [breadcrumbManager]);

    return (
        <form className={styles[`location-filter`]}>
            {(state?.hasBreadcrumbs || distance) && (
                <Button
                    buttonStyle={Button.STYLE.TEXT}
                    className={styles[`location-filter__reset`]}
                    type="button"
                    iconComponent={LazyIconXCircle}
                    iconRight
                    label={resetAllLabel}
                    ariaLabel={resetAllAriaLabel}
                    onClick={handleResetAll}
                    analytics={{
                        componentName: 'Filter Sort Panel',
                        componentTitle: 'filter_panel',
                        interactionType: 'filter_reset',
                        allFilters: searchParams,
                    }}
                />
            )}
            <fieldset
                className={classNames(
                    styles[`location-filter__fieldset`],
                    styles[`location-filter__wrapper`],
                )}
            >
                <legend className={classNames(styles[`location-filter__legend`], 'type-t3')}>
                    {viewState === 'result-list' ? filterLegendLabelList : filterLegendLabelMap}
                </legend>
                {viewState === 'result-list' && (
                    <fieldset className={styles[`location-filter__fieldset`]}>
                        <legend
                            className={classNames(
                                styles[`location-filter__group-legend`],
                                'type-e1',
                            )}
                        >
                            Sort By:
                        </legend>
                        <RadioGroup
                            className={styles[`location-filter__radio-group`]}
                            value={sortValue}
                            onChange={handleSort}
                        >
                            {sortCriteria.map(([name]) => {
                                if (name === 'Distance' && disabledDistanceOptions) {
                                    return null;
                                }
                                return <Radio key={name} value={name} label={name} />;
                            })}
                        </RadioGroup>
                    </fieldset>
                )}
                <fieldset className={styles[`location-filter__fieldset`]}>
                    <legend
                        className={classNames(styles[`location-filter__group-legend`], 'type-e1')}
                    >
                        Filter By:
                    </legend>
                    {!disabledDistanceOptions && (
                        <RadioGroup
                            label="Distance"
                            labelClass={styles[`location-filter__radio-group-label`]}
                            value={distance}
                            onChange={(value) => {
                                setDistance(value);
                                handleSingleFilter(
                                    { distance: value },
                                    {
                                        componentTitle: 'Distance',
                                        actionLabel: value ? `Within ${value} miles` : 'Any',
                                    },
                                );
                            }}
                        >
                            <Radio value="" label="Any" />
                            <Radio value="2" label="Within 2 miles" />
                            <Radio value="10" label="Within 10 miles" />
                            <Radio value="25" label="Within 25 miles" />
                            <Radio value="50" label="Within 50 miles" />
                        </RadioGroup>
                    )}
                </fieldset>
                <Accordion
                    type={'multiple'}
                    className={styles[`location-filter__accordion`]}
                    accordionItems={accordionItems}
                    noScrollLogic
                />
            </fieldset>
        </form>
    );
};

LocationFilterDesktop.propTypes = {};

export default LocationFilterDesktop;
